import React, { useMemo, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Teachr from "../../Objects/models/Teachr";
import DialogLayout from "../../Components/DialogLayout";
import SessionDescriptionRepository from "../../Repositories/SessionDescriptionRepository";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../Redux/Reducers/MessageReducers";
import { isDepositEnabled } from "../../config/environment.config";
import { describeMonthlyQuota } from "../../Utils/monthlyQuota";
import { MAX_SESSIONS_MONTH, SessionRow, nextSessionRow, sortSessionRows } from "../DeclarationCourse/SessionList";
import { fetchExternalOverlap } from "../DeclarationCourse/sessionConflicts";
import { DV2 } from "../DeclarationV2/theme";
import { euros, monthName } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import WizardShell from "../DeclarationV2/layout/WizardShell";
import { Button, Card, FieldError, FieldLabel, Hint, Notice, SectionTitle, TextLink, fieldStyle, useDv2Theme } from "../DeclarationV2/layout/Ui";
import SessionCard from "../DeclarationV2/components/SessionCard";
import DepositPanel from "../DeclarationV2/components/DepositPanel";
import SessionDescriptionPanel from "../DeclarationV2/components/SessionDescriptionPanel";
import { AmountsBlock, scopeLabel } from "../DeclarationV2/components/AmountsBlock";
import { computeAmounts } from "../DeclarationV2/components/amounts";
import { relationRemuneration } from "../DeclarationV2/components/relationTeachr";
import { RelationRateField, RelationRemunerationBlock } from "../DeclarationV2/components/RelationRemunerationBlock";
import { enforceEarliestStart, firstSelectableDay } from "../DeclarationV2/components/planningRules";
import { editsFromDraft } from "../BrouillonsV2/types";
import { draftCeiling, draftHours, draftIssuesSummary, draftPeriod, draftQuota, hasDraftIssues } from "../BrouillonsV2/drafts";
import { CourseEditLocationState, CourseEdits, EditableCourse } from "./types";
import { courseEditIssues, hasPastSession, needsDepositAttestation } from "./courseEditRules";
import submitCourseEdit, { describeCourseEditError } from "./api/submitCourseEdit";
import { coursesPath } from "./paths";

interface Props {
    course: EditableCourse;
    teachr: Teachr;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Modifier un cours à venir ou du mois, en page (même gabarit que la
 * modification d'un brouillon, maquettes 1h et 2h) : une carte par séance
 * avec l'erreur sur la carte fautive, prix horaire payé et « Vous recevez »,
 * acompte (attestation à refaire seulement s'il change), déroulement propre
 * au cours, résumé des erreurs près des boutons avec « Y aller ».
 * « Enregistrer » reprend les contrôles et la chaîne d'envoi de la modale
 * existante : chevauchement avec un autre cours du prof, confirmation si une
 * séance est déjà passée, puis mise à jour du cours et de sa proposition.
 */
const CourseEditForm = ({ course, teachr }: Props) => {
    const { fontFamily } = useDv2Theme();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const arrival = (location.state as CourseEditLocationState | null) ?? null;

    const [edits, setEdits] = useState<CourseEdits>(() => editsFromDraft(course));
    const [showIssues, setShowIssues] = useState(false);
    const [overlap, setOverlap] = useState<{ key: string; message: string } | null>(null);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [overOpen, setOverOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [genericDescription, setGenericDescription] = useState<string | null>(null);

    const current = useMemo<EditableCourse>(() => ({ ...course, ...edits }), [course, edits]);
    const dirty = useMemo(() => JSON.stringify(edits) !== JSON.stringify(editsFromDraft(course)), [edits, course]);
    const issues = useMemo(() => courseEditIssues(current, teachr), [current, teachr]);
    const hours = draftHours(current);
    const amounts = computeAmounts(current.price, hours, teachr, { zip: course.student.zip }, current.deposit.enabled ? current.deposit.amount : 0);
    // relation Teach'r : tarif fixé par la plateforme, seule la rémunération du prof est montrée
    const remuneration = relationRemuneration(course.raw, hours);
    const depositAllowed = course.isTeachrContact && isDepositEnabled() && !(teachr as any)?.scoop;
    const minDate = firstSelectableDay(course.student.activationDate);
    const rows = sortSessionRows(current.sessions);
    // Repères du mois (compteur de 8 et plafond) : seulement pour un cours du mois en cours, où l'API les renvoie
    const quotaInfo = draftQuota(course);
    const quota = describeMonthlyQuota(quotaInfo);
    const ceiling = draftCeiling(current, teachr);
    const periodStart = draftPeriod(current).start;
    const inCurrentMonth = !!periodStart && periodStart.format("YYYY-MM") === moment().format("YYYY-MM");
    const showMonthNotice = inCurrentMonth && (quotaInfo !== null || ceiling.declared > 0);
    const attestationNeeded = needsDepositAttestation(current);

    const issueByKey = new Map<string, string>(issues.rows.map((i) => [i.key, i.message]));
    if (overlap && !issueByKey.has(overlap.key)) issueByKey.set(overlap.key, overlap.message);

    const goToRow = (key: string) => {
        setExpandedKey(key);
        document.getElementById(`dv2-session-${key}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const setRows = (next: SessionRow[]) => {
        setOverlap(null);
        setEdits((e) => ({ ...e, sessions: enforceEarliestStart(next, course.student.activationDate) }));
    };
    const changeRow = (key: string, patch: Partial<SessionRow>) => setRows(current.sessions.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    const removeRow = (key: string) => setRows(current.sessions.filter((row) => row.key !== key));
    const addRow = () => {
        if (current.sessions.length >= MAX_SESSIONS_MONTH) return;
        const row = nextSessionRow(current.sessions);
        setRows([...current.sessions, row]);
        setExpandedKey(row.key);
    };
    const canRemove = course.multiple && current.sessions.length > course.minSessions;

    const leave = () => (arrival?.fromList ? navigate(-1) : navigate(coursesPath()));
    const back = () => (dirty ? setLeaveOpen(true) : leave());

    // ── Enregistrer : contrôles, chevauchement, confirmation « déjà passé », envoi ──
    const send = async (over: boolean) => {
        setBusy(true);
        try {
            let generic = genericDescription;
            if (generic === null) {
                try {
                    const response = await SessionDescriptionRepository.get(teachr.id as number);
                    generic = response?.sessionDescription?.description ?? null;
                } catch {
                    generic = null;
                }
            }
            await submitCourseEdit(current, teachr, generic, { over });
            dispatch({ type: SHOW_SUCCESS, payload: `Votre ${vocab.course()} a bien été modifié${vocab.course() === "prestation" ? "e" : ""}.` });
            leave();
        } catch (err: any) {
            console.error(err);
            dispatch({ type: SHOW_ERROR, payload: describeCourseEditError(err), title: "Attention !" });
        } finally {
            setBusy(false);
            setOverOpen(false);
        }
    };

    const save = async () => {
        setShowIssues(true);
        if (hasDraftIssues(issues)) {
            const first = issues.rows[0]?.key;
            if (first) goToRow(first);
            return;
        }
        setBusy(true);
        try {
            // chevauchement avec un autre cours du prof : le cours modifié est exclu, ses séances vont être remplacées
            const found = await fetchExternalOverlap(teachr?.id, rows, course.id);
            if (found) {
                const row = rows[found.index - 1];
                const message = `Chevauche ${vocab.theCourse()} déjà déclaré de ${found.existingSlot} avec ${found.studentName}. Décalez le début ou la date de cette séance.`;
                if (row) {
                    setOverlap({ key: row.key, message });
                    goToRow(row.key);
                } else {
                    dispatch({ type: SHOW_ERROR, payload: message, title: "Créneau déjà occupé" });
                }
                return;
            }
        } finally {
            setBusy(false);
        }
        if (course.chapterOver || hasPastSession(current)) {
            setOverOpen(true);
            return;
        }
        await send(false);
    };

    const summary = showIssues ? draftIssuesSummary(current, issues) ?? overlap?.message ?? null : overlap?.message ?? null;
    const firstIssueKey = issues.rows[0]?.key ?? overlap?.key ?? null;
    const subtitle = `${course.student.firstName} ${course.student.lastName}${course.subjectLabel ? `, ${course.subjectLabel.toLowerCase()}` : ""}, ${course.multiple ? vocab.multipleCoursesTitle().toLowerCase() : vocab.singleCourseTitle().toLowerCase()}`;

    return (
        <>
            <DialogLayout
                title="Quitter sans enregistrer ?"
                text={
                    <p style={{ fontFamily, fontSize: 15, lineHeight: 1.55, color: DV2.text, margin: 0 }}>
                        Vos modifications ne sont pas conservées tant que vous n'avez pas enregistré. Si vous quittez maintenant, {vocab.theCourse()} garde ses informations actuelles.
                    </p>
                }
                valid="Quitter"
                cancel="Rester"
                open={leaveOpen}
                handleSubmit={leave}
                handleClose={() => setLeaveOpen(false)}
            />
            <DialogLayout
                title={`${capitalize(vocab.course())} déjà ${vocab.past()}`}
                text={
                    <p style={{ fontFamily, fontSize: 15, lineHeight: 1.55, color: DV2.text, margin: 0 }}>
                        Une séance de {vocab.thisCourse()} est déjà passée. Une fois enregistré{vocab.course() === "prestation" ? "e" : ""},{" "}
                        <strong>{vocab.thisCourse()} ne pourra plus être modifié{vocab.course() === "prestation" ? "e" : ""} ni supprimé{vocab.course() === "prestation" ? "e" : ""}</strong>.
                    </p>
                }
                valid="Enregistrer"
                cancel="Retour"
                open={overOpen}
                load={busy}
                handleSubmit={() => send(true)}
                handleClose={() => { if (!busy) setOverOpen(false); }}
            />
            <WizardShell
                title={`Modifier ${vocab.theCourse()}`}
                subtitle={subtitle}
                onBack={back}
                narrow
                footer={{
                    onPrevious: back,
                    previousLabel: `Retour à mes ${vocab.courses()}`,
                    onNext: save,
                    nextLabel: "Enregistrer les modifications",
                    nextLoading: busy,
                    nextDisabled: busy || !!course.lockReason,
                    extra: summary ? (
                        <Notice tone="danger" id="ce2-summary" action={firstIssueKey ? <TextLink label="Y aller" onClick={() => goToRow(firstIssueKey)} /> : undefined}>{summary}</Notice>
                    ) : dirty ? (
                        <span style={{ fontSize: 13, color: DV2.warning, fontWeight: 700 }}>Modifications non enregistrées</span>
                    ) : undefined,
                }}
            >
                {course.lockReason && (
                    <Notice tone="warning">{course.lockReason} : {vocab.thisCourse()} ne peut plus être modifié{vocab.course() === "prestation" ? "e" : ""}.</Notice>
                )}

                {/* Repères du mois pour l'élève, comme la bannière de la modale existante */}
                {showMonthNotice && (
                    <Notice tone={quota.tone === "danger" || ceiling.exceeds ? "warning" : "info"}>
                        <b>{capitalize(monthName(moment()))}, {course.student.firstName} :</b>{" "}
                        {quotaInfo !== null ? `${quota.headline} (${quota.detail}), ` : ""}
                        {course.isRelationTeachr
                            ? `plafond mensuel de l'avance immédiate ${ceiling.exceeds ? "dépassé" : "respecté"}.`
                            : <>{euros(ceiling.declared)} déjà déclarés sur un plafond de {euros(ceiling.ceiling, 0)}.</>}
                    </Notice>
                )}

                <div className="flex flex-wrap gap-2 justify-between items-center">
                    <div style={{ fontSize: 13, color: DV2.muted }}>
                        {course.multiple
                            ? "Vos séances telles qu'elles sont enregistrées. Touchez une séance ou son crayon pour l'ajuster."
                            : `Votre ${vocab.course()} tel qu'il est enregistré. Touchez-le ou son crayon pour l'ajuster.`}
                    </div>
                    {course.multiple && (
                        <Button label="+ Ajouter une séance" variant="secondary" size="sm" onClick={addRow} disabled={current.sessions.length >= MAX_SESSIONS_MONTH} />
                    )}
                </div>

                <div className="flex flex-col gap-3">
                    {rows.map((row, index) => (
                        <SessionCard
                            key={row.key}
                            row={row}
                            index={index}
                            issue={showIssues || overlap?.key === row.key ? issueByKey.get(row.key) : undefined}
                            expanded={expandedKey === row.key}
                            onToggle={() => setExpandedKey(expandedKey === row.key ? null : row.key)}
                            onChange={(patch) => changeRow(row.key, patch)}
                            onRemove={canRemove ? () => removeRow(row.key) : undefined}
                            minDate={minDate}
                        />
                    ))}
                    {showIssues && issues.sessions && <Notice tone="danger">{issues.sessions}</Notice>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <Card style={{ display: "flex", flexDirection: "column", gap: 12, padding: 22 }}>
                        <SectionTitle>{course.isRelationTeachr ? "Votre tarif" : "Prix de l'heure"}</SectionTitle>
                        {course.isRelationTeachr ? (
                            <RelationRateField remuneration={remuneration} firstName={course.student.firstName} error={showIssues ? issues.price : null} />
                        ) : (
                        <div>
                            <FieldLabel htmlFor="ce2-price" hint={`par ${vocab.yourClient()}`}>Prix horaire payé</FieldLabel>
                            <div style={{ position: "relative" }}>
                                <input
                                    id="ce2-price"
                                    type="number"
                                    inputMode="decimal"
                                    min={6}
                                    max={138}
                                    step="0.5"
                                    value={current.price}
                                    disabled={!course.isTeachrContact}
                                    onChange={(e) => setEdits((ed) => ({ ...ed, price: e.target.value }))}
                                    aria-invalid={showIssues && !!issues.price}
                                    aria-describedby={showIssues && issues.price ? "ce2-price-error" : undefined}
                                    style={{ ...fieldStyle(showIssues && !!issues.price), fontFamily, fontSize: 20, fontWeight: 800, paddingRight: 110 }}
                                />
                                <span aria-hidden style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: DV2.muted }}>€ par heure</span>
                            </div>
                            {showIssues && issues.price ? <FieldError id="ce2-price-error">{issues.price}</FieldError> : (
                                <Hint>
                                    C'est ce que paie {vocab.yourClient()} pour une heure, avance immédiate déduite, pas ce que vous recevez.{" "}
                                    {!course.isTeachrContact ? `Tarif fixé avec ${course.student.firstName} dans sa fiche.` : `Tarif enregistré : ${euros(course.price)}.`}
                                </Hint>
                            )}
                        </div>
                        )}
                    </Card>
                    {course.isRelationTeachr
                        ? <RelationRemunerationBlock remuneration={remuneration} scope={scopeLabel(course.multiple ? rows.length : 1)} />
                        : <AmountsBlock amounts={amounts} scope={scopeLabel(course.multiple ? rows.length : 1)} showDeposit={depositAllowed} />}
                </div>

                {depositAllowed && (
                    <>
                        {current.deposit.enabled && !attestationNeeded && course.existingDeposit.hasAttestation && (
                            <Notice tone="success">
                                Attestation sur l'honneur d'acompte signée{course.existingDeposit.attestationSignedAt ? ` le ${moment(course.existingDeposit.attestationSignedAt).format("DD/MM/YYYY")}` : ""}
                                {course.existingDeposit.proofUrls.length > 0 ? `, ${course.existingDeposit.proofUrls.length > 1 ? "preuves déjà enregistrées" : "preuve déjà enregistrée"}` : ""}.
                                {" "}Toute modification du montant ou de la date en produira une nouvelle.
                            </Notice>
                        )}
                        <DepositPanel
                            teachr={teachr}
                            child={{ clientId: course.student.clientId, childFirstName: course.student.firstName, childLastName: course.student.lastName, zip: course.student.zip }}
                            value={current.deposit}
                            onChange={(deposit) => setEdits((e) => ({ ...e, deposit }))}
                            ceiling={hours * (parseFloat(current.price) || 0)}
                            minDate={course.student.activationDate ? moment(course.student.activationDate).format("YYYY-MM-DD") : null}
                            errors={showIssues ? issues.deposit : {}}
                            multiple={course.multiple}
                        />
                    </>
                )}

                <SessionDescriptionPanel
                    teachr={teachr}
                    value={current.sessionDescriptionOverride}
                    onChange={(value) => setEdits((e) => ({ ...e, sessionDescriptionOverride: value }))}
                    onGenericLoaded={setGenericDescription}
                    error={showIssues ? issues.description ?? undefined : undefined}
                />

                {showIssues && hasDraftIssues(issues) && issues.all.length > 1 && (
                    <Card tint style={{ padding: "14px 18px" }}>
                        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>{capitalize(`${issues.all.length} points à corriger`)}</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: DV2.textSoft, lineHeight: 1.5 }}>
                            {issues.all.map((message) => <li key={message}>{message}</li>)}
                        </ul>
                    </Card>
                )}
            </WizardShell>
        </>
    );
};

export default CourseEditForm;
